import {
	Button,
	Card,
	CardContent,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
	Box,
} from "@mui/material";
import { toast } from "react-toastify";
import config from "../../utils/config";
import moment from "moment";
import { useEffect, useState } from "react";
import axios from "axios";
import { useDebouncedCallback } from "use-debounce";
import { useParams } from "react-router-dom";

function AddSharedEvent(props) {
	const { editEventId } = useParams();

	const [eventList, setEventList] = useState([]);
	const [sharedEvent, setSharedEvent] = useState("");
	const [extraPrice, setExtraPrice] = useState(0);
	const [extraEsnCardPrice, setExtraEsnCardPrice] = useState(0);
	const [extraDiscountPrice, setExtraDiscountPrice] = useState(0);

	useEffect(() => {
		if (editEventId) {
			props.setTitle("Edit Shared Event");
		} else {
			props.setTitle("Join Shared Event");
		}
	}, [editEventId]); //eslint-disable-line

	useEffect(() => {
		fetchSharedEventList();

		if (editEventId) {
			fetchEventInfo();
		} else {
			resetState();
		}
	}, [props.organization, editEventId]); //eslint-disable-line

	const resetState = () => {
		setSharedEvent("");
		setExtraPrice(0);
		setExtraEsnCardPrice(0);
		setExtraDiscountPrice(0);
	};

	const fetchEventInfo = () => {
		axios
			.get(`${config.API_ENDPOINT}/events/shared/${props.organization}/${editEventId}`, { headers: { "x-access-token": localStorage.getItem("access-token") } })
			.then(res => {
				let event = res.data;
				setSharedEvent(event.parentEvent);
				setExtraPrice(event.extraPrice);
				setExtraEsnCardPrice(event.extraEsnCardPrice);
				setExtraDiscountPrice(event.extraDiscountPrice);
			})
			.catch(err => {
				switch (err.response?.status) {
					case 401:
						localStorage.removeItem("access-token");
						window.location.replace("/login");
						break;
					default:
						toast.error("An unexpected error has occurred");
						break;
				}
			});
	}

	const fetchSharedEventList = () => {
		axios
			.get(`${config.API_ENDPOINT}/events/shared/all`, { headers: { "x-access-token": localStorage.getItem("access-token") } })
			.then(res => setEventList(res.data))
			.catch(err => {
				switch (err.response?.status) {
					case 401:
						localStorage.removeItem("access-token");
						window.location.replace("/login");
						break;
					default:
						toast.error("An unexpected error has occurred");
						break;
				}
			});
	}

	const submit = useDebouncedCallback(() => {
		if (sharedEvent === "") {
			toast.warning("You must choose a shared event to join");
			return;
		}

		let submission = {};
		let url = "";

		if (editEventId) {
			submission = {
				extraPrice,
				extraEsnCardPrice,
				extraDiscountPrice,
			};

			url = `${config.API_ENDPOINT}/events/shared/${props.organization}/${editEventId}`;
		} else {
			submission = {
				organization: props.organization,
				parentEvent: sharedEvent._id,
				extraPrice,
				extraEsnCardPrice,
				extraDiscountPrice,
			};

			url = `${config.API_ENDPOINT}/events/shared/create`;
		}

		axios
			.post(url, submission, { headers: { "x-access-token": localStorage.getItem("access-token") } })
			.then(() => {
				toast.success("Success!");
				if (editEventId == null) {
					resetState();
				}
			})
			.catch(err => {
				switch (err.response?.status) {
					case 401:
						localStorage.removeItem("access-token");
						window.location.replace("/login");
						break;
					case 409:
						toast.warning("Your organization has already joined this shared event.");
						break;
					default:
						toast.error("An unexpected error has occurred");
						break;
				}
			});
	}, 500, { leading: true });

	const copyIframe = async () => {
		let text = `<iframe frameborder="0" scrolling="yes" src="${config.WEB_ENDPOINT}/iframes/event-details/shared/${props.organization}/${editEventId}" style=" min-height: 700px; overflow-y: scroll; width: 100%; "></iframe>`;
		await navigator.clipboard.writeText(text);
		toast.success("Copied!");
	}

	if (props.user.accessLevel > 3) return null;

	return (
		<div>
			<Box sx={{ mb: 4, pb: 3, borderBottom: "1px solid rgba(19, 33, 68, 0.1)" }}>
				<Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>{editEventId ? "Edit Shared Event" : "Join Shared Event"}</Typography>
				<Typography variant="body2" sx={{ color: "text.secondary" }}>Configure pricing and details for a shared event</Typography>
			</Box>

			<Grid container spacing={3}>
				<Grid item xs={12} md={6}>
					<Card>
						<CardContent>
							<Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Event Details</Typography>
							{editEventId ? null : (
								<FormControl fullWidth required sx={{ mb: 2 }}>
									<InputLabel>Shared Event</InputLabel>
									<Select
										label="Shared Event"
										value={sharedEvent}
										onChange={(e) => {
											setSharedEvent(e.target.value);
											setExtraPrice(0);
											setExtraEsnCardPrice(0);
											setExtraDiscountPrice(0);
										}}
									>
										{eventList.map((event) => (
											<MenuItem key={event._id} value={event}>{event.name}</MenuItem>
										))}
									</Select>
								</FormControl>
							)}

							{sharedEvent !== "" && (
								<>
									<Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>Pricing</Typography>
									<TextField
										variant="outlined"
										fullWidth
										label="Extra Price"
										name="extraPrice"
										type="number"
										value={extraPrice}
										required
										onChange={(e) => setExtraPrice(e.target.value !== "" ? parseFloat(e.target.value) : null)}
										inputProps={{ step: "0.01" }}
										sx={{ mb: 2 }}
									/>
									{sharedEvent.esnCardRequired ? null : (
										<TextField
											variant="outlined"
											fullWidth
											label="Extra Price with ESNcard"
											name="extraEsnCardPrice"
											type="number"
											value={extraEsnCardPrice}
											required
											onChange={(e) => setExtraEsnCardPrice(e.target.value !== "" ? parseFloat(e.target.value) : null)}
											inputProps={{ step: "0.01" }}
											sx={{ mb: 2 }}
										/>
									)}
									{sharedEvent.esnCardBuyPrice ? (
										<TextField
											variant="outlined"
											fullWidth
											label="Extra Price with Discount"
											name="extraDiscountPrice"
											type="number"
											value={extraDiscountPrice}
											required
											onChange={(e) => setExtraDiscountPrice(e.target.value !== "" ? parseFloat(e.target.value) : null)}
											inputProps={{ step: "0.01" }}
										/>
									) : null}
								</>
							)}
						</CardContent>
					</Card>
				</Grid>

				<Grid item xs={12} md={6}>
					<Card>
						<CardContent>
							<Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Preview</Typography>
							{editEventId && (
								<Box sx={{ display: "flex", gap: 1, mb: 3 }}>
									<Button
										variant="outlined"
										size="small"
										onClick={copyIframe}
										sx={{ textTransform: "none", fontWeight: 600 }}
									>
										Copy Embed Code
									</Button>
									<Button
										variant="outlined"
										size="small"
										sx={{ textTransform: "none", fontWeight: 600 }}
										onClick={() => {
											window.open(
												`${config.WEB_ENDPOINT}/iframes/event-details/shared/${props.organization}/${editEventId}/internal`,
												"_blank"
											);
										}}
									>
										Open Internal Sale
									</Button>
								</Box>
							)}
							{sharedEvent !== "" ? (
								<>
									{sharedEvent.imageURL ? (
										<img
											alt="Event"
											height={150}
											width={"100%"}
											style={{ objectFit: "contain", marginBottom: 16 }}
											src={`${config.IMG_ENDPOINT}/uploads/${sharedEvent.imageURL}`}
										/>
									) : (
										<Box
											sx={{
												height: 150,
												width: "100%",
												backgroundColor: "rgba(19, 33, 68, 0.05)",
												borderRadius: 1,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												fontSize: 12,
												color: "text.secondary",
												mb: 2,
											}}
										>
											No image
										</Box>
									)}
									<Typography variant="h6" sx={{ textAlign: "center", mb: 2 }}>{sharedEvent.name}</Typography>
									<Typography variant="body2" sx={{ mb: 2 }}>
										<strong>{sharedEvent.datetimeEnd ? "From: " : "Date: "}</strong>
										{sharedEvent.datetime ? moment(sharedEvent.datetime).format("dddd, DD/MM/YYYY HH:mm") : ""}
									</Typography>
									{sharedEvent.datetimeEnd && (
										<Typography variant="body2" sx={{ mb: 2 }}>
											<strong>To: </strong>
											{moment(sharedEvent.datetimeEnd).format("dddd, DD/MM/YYYY HH:mm")}
										</Typography>
									)}
									<Typography variant="body2" sx={{ mb: 2 }}>
										<strong>Location: </strong>
										{sharedEvent.location}
									</Typography>
									<Box sx={{ mb: 2, fontSize: "18px", fontWeight: 700, textAlign: "right" }}>
										{((sharedEvent.regularPrice || 0) + (extraPrice ?? 0)).toFixed(2)}€
									</Box>
									<Typography variant="body2" sx={{ mb: 1, textAlign: "right" }}>
										{!sharedEvent.esnCardRequired && sharedEvent.esnCardPrice != null
											? `${((sharedEvent.esnCardPrice || 0) + (extraEsnCardPrice ?? 0)).toFixed(2)}€ with ESNcard`
											: "Only for students with ESNcard"}
									</Typography>
									{sharedEvent.esnCardBuyPrice && (
										<Typography variant="body2" sx={{ mb: 2, textAlign: "right" }}>
											{((sharedEvent.esnCardBuyPrice || 0) + (extraDiscountPrice ?? 0)).toFixed(2)}€ with discount
										</Typography>
									)}
								</>
							) : (
								<Typography variant="body2" sx={{ color: "text.secondary" }}>Select an event to see preview</Typography>
							)}
							<Button
								fullWidth
								variant="contained"
								onClick={submit}
								sx={{ mt: 3, textTransform: "none", fontWeight: 600 }}
							>
								{editEventId ? "Update Event" : "Submit"}
							</Button>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</div>
	);
}

export default AddSharedEvent;
